import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  resolveProfileGuardPath,
  type ProfileGuardOptions
} from '@link/share/utils';

/**
 * Post-auth navigation that routes through the profile-completeness gate
 * when `profileGuard` is provided, and replace-navigates to the resolved
 * target. Only the latest call navigates: results that resolve after the
 * component unmounted or after a newer call are dropped.
 */
export function useProfileGuardNavigate(profileGuard?: ProfileGuardOptions) {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const callSeqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(
    async (target: string) => {
      const callSeq = ++callSeqRef.current;
      const resolved = profileGuard
        ? await resolveProfileGuardPath(profileGuard, target)
        : target;
      if (mountedRef.current && callSeq === callSeqRef.current) {
        navigate(resolved, { replace: true });
      }
    },
    [navigate, profileGuard]
  );
}
